import Keycloak, { KeycloakInstance } from 'keycloak-js';

import { getJoinUrl } from 'src/constants/config.constants';
import { KeycloakProfileWithAttributes } from 'src/interfaces/user.types';
import { UserRoles } from 'src/interfaces/user-role.enum';
import { getAmpConfig } from 'src/services/amp-config.service';
import { resetIsInitialAssignmentsMessageShown } from 'src/services/messages.service';

export const getKeycloakConfig = () => ({
  realm: 'Amplify',
  url: getAmpConfig().auth,
  clientId: 'learning-home',
});

const keycloakInitConfig = {
  checkLoginIframe: true,
  onLoad: 'check-sso' as const,
};

class AuthService {
  private _kc: KeycloakInstance | null = null;
  private tokenRefreshInterval!: number;

  async init() {
    this._kc = Keycloak(getKeycloakConfig());

    try {
      const authenticated = await this._kc.init(keycloakInitConfig);

      if (!authenticated) {
        window.location.href = getJoinUrl();
        return;
      }

      await this._kc.loadUserProfile();

      window.kc = this._kc;
    } catch (e) {
      return this._kc.logout();
    }

    this.tokenRefreshInterval = window.setInterval(async () => {
      try {
        const refreshed = await this.updateToken();
        if (refreshed) {
          const event = new CustomEvent('tokenRefreshed');
          window.dispatchEvent(event);
        }
      } catch (e) {
        console.error(e);
        this.logout();
      }
    }, 10000);
  }

  getKC() {
    return this._kc ?? ({} as KeycloakInstance);
  }

  updateToken() {
    // if token expires within 30 seconds - refresh it
    return this._kc?.updateToken(30);
  }

  getAccessToken() {
    return this._kc?.token ?? '';
  }

  getSessionId() {
    return this._kc?.sessionId ?? '';
  }

  getUserBk() {
    return this._kc?.subject ?? '';
  }

  // needed for amp-navigation
  getParsedToken() {
    return this._kc?.tokenParsed ?? {};
  }

  getUserProfile() {
    return (this._kc?.profile ?? {}) as KeycloakProfileWithAttributes;
  }

  logout() {
    clearInterval(this.tokenRefreshInterval);
    resetIsInitialAssignmentsMessageShown();
    this._kc?.logout();
  }

  hasRole(searchRole: UserRoles) {
    return !!this._kc?.tokenParsed?.realm_access?.roles.some(
      (role) => role.indexOf(searchRole) >= 0,
    );
  }

  getRole(): UserRoles {
    return this.hasRole(UserRoles.TEACHER)
      ? UserRoles.TEACHER
      : UserRoles.STUDENT;
  }

  isTeacher(): boolean {
    return this.getRole() === UserRoles.TEACHER;
  }

  isStudent(): boolean {
    return this.getRole() === UserRoles.STUDENT;
  }
}

const authService = new AuthService();
export default authService;
